import { Close } from "@mui/icons-material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TotalDoseDisplay from "src/shared/TotalDoseDisplay";
import { desktopSx } from "src/utils/styles/modalStyles";
import DosageInstruction from "../DosageInstruction";
import { PrescriptionMedicationInstruction } from "src/api/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDosageInstructions } from "src/hooks/useDosageInstructions";
import { useUpdatePrescriptionInstructionsMutation } from "src/api/services/prescriptionsApi";
import { useGetDaysSupplyQuery } from "src/api/services/daysSupplyApi";
import { usePrescription } from "src/context/PrescriptionProvider";

interface Props {
  open: boolean;
  onClose: () => void;
  details: PrescriptionMedicationInstruction;
}

const UpdateInstructionsModal = ({ open, onClose, details }: Props) => {
  const [updateInstructions, { isLoading }] =
    useUpdatePrescriptionInstructionsMutation();
  const [dosages, setDosages] = useState<string[]>([]);
  const { instructions, template } = useDosageInstructions(
    details.medications[0] ?? null,
    details.pharmacy.name,
    dosages,
  );
  const productId = usePrescription((p) => p.product_id);
  const patientId = usePrescription((p) => p.patient_id);

  const { data } = useGetDaysSupplyQuery({
    product_id: productId,
    patient_id: patientId,
  });

  const daysSupply = useMemo(() => {
    return data?.days_supply ?? 0;
  }, [data?.days_supply]);

  const handleClose = useCallback(() => {
    setDosages([]);
    onClose();
  }, [onClose]);

  const medication = useMemo(() => {
    if (details == null) return null;

    const name = details?.medications[0]?.display_name ?? "";
    const unit = details?.medications[0]?.dosage.replace(/\d+([,.]\d+)?/g, "");
    const totalDose = details?.medications.reduce(
      (acc, item) => (acc += parseFloat(item.dosage)),
      0,
    );

    return { name, totalDose, unit };
  }, [details]);

  const totalDosageLeft = useMemo(() => {
    if (details == null) return null;
    if (medication == null) return null;

    const totalDosages = dosages.reduce((acc, dosage) => {
      return acc + parseFloat(dosage) || 0;
    }, 0);

    return medication?.totalDose - totalDosages * 4;
  }, [details, dosages, medication]);

  const handleConfirm = useCallback(() => {
    const prescriptionId = details.id;
    const currentInstructions = details?.instructions;

    if (prescriptionId == null || currentInstructions == null) return;

    updateInstructions({
      prescriptionId,
      body: {
        instructions: instructions.map((i, index) => ({
          ...currentInstructions[index],
          prescription_id: prescriptionId,
          dosage: i.dosage,
          amount: i.amount,
          duration: `${i.interval_count} ${i.interval}s`,
          interval: i.interval,
          interval_count: i.interval_count,
          value: template
            .replace("{{week}}", i.week ?? "")
            .replace("{{amount}}", i.amount)
            .replace("{{dosage}}", i.dosage)
            .replace("{{duration}}", `${i.interval_count} ${i.interval}s`),
        })),
      },
    })
      .unwrap()
      .then(handleClose);
  }, [
    details.id,
    details?.instructions,
    handleClose,
    instructions,
    template,
    updateInstructions,
  ]);

  useEffect(() => {
    if (details != null) {
      setDosages(
        details?.instructions.length > 0
          ? details?.instructions.map((i) => i.dosage)
          : Array(Math.round(daysSupply / 30)).fill(""),
      );
    }
  }, [daysSupply, details]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Stack sx={desktopSx} gap={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontWeight={700} variant="h5">
            Update Dosage
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>

        {medication ? (
          <>
            <TotalDoseDisplay
              product={medication.name}
              total={medication.totalDose}
              unit={medication.unit}
              totalDosageLeft={totalDosageLeft}
            />
            {instructions.map((instruction, index) => (
              <DosageInstruction
                medicationName={medication?.name}
                template={template}
                instruction={instruction}
                index={index}
                key={index}
                setDosages={setDosages}
              />
            ))}
          </>
        ) : null}

        <Stack direction="row" gap={1} mt="auto">
          <Button fullWidth loading={isLoading} onClick={handleConfirm}>
            Confirm
          </Button>
          <Button fullWidth onClick={handleClose} variant="secondary">
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default UpdateInstructionsModal;
